/**
 * The seeded opening problem (§6).
 *
 * Someone needs something, and it's small: a person, a need, an amount, a
 * deadline. The shape is fixed; the specifics come from the dependent's
 * card and the seed. Solvability is seeded in three flavors, weighted
 * toward "solvable dirty": the legal options don't quite reach in time and
 * the game leaves the decision with the player.
 *
 * The amount is set against what the jobs can actually earn before the
 * deadline, so "clean" and "dirty" mean what they say in this world, with
 * this start time, on this day of the week. "None" draws its amount from
 * the same range as "dirty" and differs in nothing the player can see.
 */

import type { Content, OpeningTemplate } from './content.js';
import type { Rng } from './rng.js';
import { dayOfWeek, MINUTES_PER_DAY, timeOf, toMinutes } from './time.js';
import { TUNING } from './tuning.js';
import type { Job, Solvability, WorldTime } from './types/index.js';

export interface OpeningDraft {
  readonly template: OpeningTemplate;
  readonly amount: number;
  readonly deadline: WorldTime;
  readonly solvability: Solvability;
  /** Cash in hand at the deadline if you work every legal shift and eat once a day. */
  readonly legalReach: number;
  /** Legal plus illegal. */
  readonly dirtyReach: number;
  /** The cold-open lines with {amount} and {deadline} filled in. */
  readonly lines: readonly string[];
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

/** How many full shifts of `job` fit between `from` and `deadline`, one per day. */
export function shiftsBefore(job: Job, from: WorldTime, deadline: WorldTime): number {
  let shifts = 0;
  for (let day = from.day; day <= deadline.day; day += 1) {
    if (!job.days.includes(dayOfWeek(day))) continue;
    const earliestStart = day === from.day ? Math.max(job.window.from, from.minute) : job.window.from;
    if (earliestStart >= job.window.to) continue;
    const end = day * MINUTES_PER_DAY + earliestStart + job.minutes;
    if (end <= toMinutes(deadline)) shifts += 1;
  }
  return shifts;
}

/** A body has needs (§9): one meal a day between now and then, at today's price. */
export function livingCost(from: WorldTime, deadline: WorldTime, priceIndex = 1): number {
  const days = Math.max(0, deadline.day - from.day);
  return days * TUNING.needs.mealPrice * priceIndex;
}

/**
 * What you could have in hand at the deadline: cash, plus every shift the
 * jobs allow, minus eating. "The honest route reaches, barely" (§6) has to
 * mean it reaches after you've fed yourself.
 */
export function reach(jobs: readonly Job[], cash: number, from: WorldTime, deadline: WorldTime): number {
  const earned = jobs.reduce((sum, job) => sum + shiftsBefore(job, from, deadline) * job.pay, 0);
  return cash + earned - livingCost(from, deadline);
}

export function deadlineLabel(t: WorldTime): string {
  const hour = Math.floor(t.minute / 60);
  const part = hour >= 17 ? 'night' : hour >= 12 ? 'afternoon' : 'morning';
  return `${WEEKDAYS[dayOfWeek(t)]} ${part}`;
}

export function generateOpening(
  content: Content,
  rng: Rng,
  dependent: string,
  startingCash: number,
  jobs: readonly Job[],
  start: WorldTime,
): OpeningDraft {
  const candidates = content.opening.problems.filter((p) => p.dependent === dependent);
  if (candidates.length === 0) throw new Error(`No opening problem for dependent "${dependent}"`);
  const template = rng.pick(candidates);

  // The dependent's relation must be stated plainly in the cold open (§6):
  // "Nadia is my sister and nothing told me." `content` already carries the
  // dependent's card (`generateOpening` doesn't need a new parameter for
  // it) — `dependent.relation` is authored once, on the NPC card, and
  // reused here rather than duplicated into every opening template.
  const dependentCard = content.npcs.find((n) => n.id === dependent);
  const relation = dependentCard?.dependent?.relation ?? '';

  const days = rng.intInclusive(template.deadlineDays.min, template.deadlineDays.max);
  const deadline = timeOf(start.day + days, template.deadlineHour);

  const w = content.opening.weights;
  const solvability = rng.weighted<Solvability>([
    ['clean', w.clean],
    ['dirty', w.dirty],
    ['none', w.none],
  ]);

  const legal = jobs.filter((j) => j.kind === 'legal');
  const legalReach = Math.floor(reach(legal, startingCash, start, deadline));
  const dirtyReach = Math.floor(reach(jobs, startingCash, start, deadline));

  // Clean: the honest route reaches, barely. Dirty and none: it doesn't,
  // and something has to bend. Same draw for both so nothing gives it away.
  let amount: number;
  if (solvability === 'clean') {
    const low = Math.max(1, Math.ceil(legalReach * 0.75));
    amount = rng.intInclusive(low, Math.max(low, legalReach));
  } else {
    // Something has to bend, but not everything: cap well short of every
    // shift and every run, so "dirty" never means perfect play.
    const low = legalReach + 1;
    const high = Math.max(low, Math.floor(dirtyReach * TUNING.opening.dirtyShareOfReach));
    amount = rng.intInclusive(low, high);
  }

  const fill = (line: string) =>
    line.replace('{amount}', `$${amount}`).replace('{deadline}', deadlineLabel(deadline)).replace('{relation}', relation);

  return {
    template,
    amount,
    deadline,
    solvability,
    legalReach,
    dirtyReach,
    lines: template.opening.map(fill),
  };
}
